import { environment } from 'environments/environment';

type Environment = typeof environment;
type Auth0Config = Environment['auth0'];
type BootstrapIdentityConfig = Environment['bootstrapIdentity'];

type RuntimeEnvironment = Pick<Partial<Environment>, 'apiUrl' | 'env'> & {
	auth0?: Partial<Auth0Config> & {
		authorizationParams?: Partial<Auth0Config['authorizationParams']>;
	};
	bootstrapIdentity?: Partial<BootstrapIdentityConfig>;
};

const runtimeConfigTimeoutMs = 5000;

export const loadRuntimeConfig = async (): Promise<void> => {
	const runtimeConfigUrl = (environment as { runtimeConfigUrl?: string })
		.runtimeConfigUrl;

	if (!runtimeConfigUrl) {
		return;
	}

	const abortController = new AbortController();
	const timeoutId = window.setTimeout(
		() => abortController.abort(),
		runtimeConfigTimeoutMs
	);

	try {
		const response = await fetch(runtimeConfigUrl, {
			cache: 'no-store',
			signal: abortController.signal,
		});

		if (!response.ok) {
			console.warn(
				`Unable to load local client runtime config from ${runtimeConfigUrl}. API returned ${response.status}. Falling back to bundled environment defaults.`
			);
			return;
		}

		const runtimeConfig = (await response.json()) as RuntimeEnvironment;
		applyRuntimeConfig(runtimeConfig);
	} catch (error) {
		console.warn(
			`Unable to load local client runtime config from ${runtimeConfigUrl}. Falling back to bundled environment defaults.`,
			error
		);
	} finally {
		window.clearTimeout(timeoutId);
	}
};

const applyRuntimeConfig = (runtimeConfig: RuntimeEnvironment): void => {
	if (runtimeConfig.apiUrl) {
		environment.apiUrl = runtimeConfig.apiUrl;
	}

	if (runtimeConfig.env) {
		environment.env = runtimeConfig.env;
	}

	if (runtimeConfig.auth0) {
		environment.auth0 = {
			...environment.auth0,
			...runtimeConfig.auth0,
			authorizationParams: {
				...environment.auth0.authorizationParams,
				...runtimeConfig.auth0.authorizationParams,
			},
		};
	}

	if (runtimeConfig.bootstrapIdentity) {
		environment.bootstrapIdentity = {
			...environment.bootstrapIdentity,
			...runtimeConfig.bootstrapIdentity,
		};
	}
};
